import {UntilDestroy} from "@ngneat/until-destroy";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Directive,
    ElementRef,
    Inject,
    QueryList,
    Renderer2,
    ViewChildren,
    ViewContainerRef
} from "@angular/core";
import {combineLatest, Observable, Subject} from "rxjs";
import {map, switchMap, takeUntil, tap} from "rxjs/operators";
import {NormalViewContainerState, SplitViewContainerState, WorkbenchLayoutManager} from "../../services/workbench-layout-manager.service";
import {SplitViewContainerConfig, ViewContainerId, WORKBENCH_SPLITVIEWCONTAINER_CONFIG} from './viewcontainers';

@Directive({selector: '[viewContainerPortal]'})
export class ViewContainerPortal {}


@Directive({selector: '[splitPortalResizer]'})
export class SplitPortalResizer {}

@UntilDestroy()
@Component({
    selector: 'wm-workbench-split-viewcontainer',
    templateUrl: './split-viewcontainer.component.html',
    styleUrls: ['./split-viewcontainer.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SplitViewContainerComponent {
    state: SplitViewContainerState;
    childViewContainerStates: {[viewContainerId: string]: NormalViewContainerState | SplitViewContainerState} = {};

    // these are the sizes of the panels in the split view container in percent.
    // the sum of all sizes must always be 100.
    sizes: number[];

    isResizing: boolean = false;

    // these hold refs to ViewContainers and also SplitViewContainers
    @ViewChildren(ViewContainerPortal, {read: ViewContainerRef}) viewContainerHostElements: QueryList<ViewContainerRef>;

    @ViewChildren(SplitPortalResizer, {read: ElementRef}) resizerElements: QueryList<ElementRef>;

    destroyViewContainer$ = new Subject<void>();

    constructor(private layoutManager: WorkbenchLayoutManager,
                private cdr: ChangeDetectorRef,
                private renderer: Renderer2,
                private hostRef: ElementRef,
                @Inject(WORKBENCH_SPLITVIEWCONTAINER_CONFIG) public config: SplitViewContainerConfig) {
    }

    ngOnInit(): void {
        this.layoutManager.subscribeToViewContainerChanges$(this.config.id).pipe(
            // takeUntil(this.destroyViewContainer$),
            tap({
                // this is necessary because otherwise the inner observables of switchMap may keep the outer observable alive forever.
                complete: () => this.destroyViewContainer$.next(undefined)
            }),
            switchMap((myContainerState: SplitViewContainerState) => {
                const childViewContainerStates: {[viewContainerId: string]: Observable<SplitViewContainerState | NormalViewContainerState>} = {};
                myContainerState.children.forEach(child => {
                    childViewContainerStates[child] = this.layoutManager.subscribeToViewContainerChanges$(child);
                });

                return combineLatest(childViewContainerStates).pipe(map(childStates => ({myContainerState, childStates})));
            }),
            takeUntil(this.destroyViewContainer$),
        ).subscribe({
            next: ({myContainerState, childStates}) => {
                this.childViewContainerStates = childStates;
                this.state = myContainerState;

                this.sizes = Array(this.state.children.length).fill(100 / (this.state.children.length));

                // this is important. we don't want Angular to destroy any child view components.
                // we do that ourselves in the layout manager
                this.detachOutlets();

                // now we can run change detection
                this.cdr.markForCheck();
                setTimeout(() => {
                    this.cdr.markForCheck();
                    this.updateOutlets();
                });
            },
            error: (err) => {
                console.error("SplitViewContainerComponent: error", err);
            },
            complete: () => {
                this.childViewContainerStates = {};
                this.state = undefined;
                this.detachOutlets();
            }
        });
    }

    ngAfterViewInit() {
        // this.initializeResizers();
        this.cdr.markForCheck();
    }

    initializeResizer(prevElem: HTMLElement, nextElem: HTMLElement, resizerElem: ElementRef, resizeCallback: (event: MouseEvent) => void) {
        let timeout: any;
        this.renderer.listen(resizerElem.nativeElement, 'mousedown', (event) => {
            this.isResizing = true;

            const moveUnlistener = this.renderer.listen(document, 'mousemove', (event) => {
                if (!this.isResizing) return;
                if (timeout) {
                    clearTimeout(timeout);
                }
                // timeout =  setTimeout(() => resizeCallback(event), 10);
                resizeCallback(event);
                this.cdr.markForCheck();
            });

            const upUnlistener = this.renderer.listen(document, 'mouseup', () => {
                this.isResizing = false;
                moveUnlistener?.();
                upUnlistener?.();
            });
        });
    }

    initializeResizers() {
        // beware: the resizerElements are only available / up to date after change detection has run.
        this.resizerElements.forEach((resizerElem: ElementRef, idx: number) => {

            // a resizer is between two children. Using the resizer affects the size of its two neighbors.
            // a child has a minimum size of 100px (if the user wants to get completely rid of it, he must hide it.)

            const prevWantedPct = this.sizes[idx];
            const nextWantedPct = this.sizes[idx + 1]; // we always have one more size than resizers, so this is safe

            // hostElems is a list of the host elements of the view containers.
            const hostElems = this.viewContainerHostElements.toArray();

            const prevElem = hostElems[idx].element.nativeElement.parentElement as HTMLElement;
            // resizers are inbetween, so there is one more host element than resizers.
            const nextElem = hostElems[idx + 1].element.nativeElement.parentElement as HTMLElement;

            if (this.config.orientation === 'horizontal') {
                // for a horizontal split view, we must calculate the distance between the left border of the
                // resizer and the left border of the left child, and the distance between the right border of the
                // resizer and the right border of the right child.
                // the ratio between these two distances is the new ratio between the sizes of the two neighbors.

                this.initializeResizer(prevElem, nextElem, resizerElem, (ev: MouseEvent) => {
                    // we need the width of the left child in pixels
                    const leftChildElemRect = prevElem.getBoundingClientRect();
                    const leftChildWidth = leftChildElemRect.width;

                    // we need the width of the right child in pixels
                    const rightChildElemRect = nextElem.getBoundingClientRect();
                    const rightChildWidth = rightChildElemRect.width;

                    // when we have all that, we can calculate the new sizes of the two neighbors in percent
                    const overAllWidth = leftChildWidth + rightChildWidth;
                    const newLeftPct = (ev.clientX - leftChildElemRect.x) / overAllWidth * 100;
                    const newRightPct = (rightChildElemRect.x + rightChildWidth - ev.clientX) / overAllWidth * 100;

                    // we "redistribute" the old percentages between the two neighbors.
                    // the sum is also the sum of the new percentages. (does not change)
                    const neighborsPct = prevWantedPct + nextWantedPct;
                    const scaledLeftPct = newLeftPct / 100 * neighborsPct;
                    const scaledRightPct = newRightPct / 100 * neighborsPct;

                    this.sizes[idx] = scaledLeftPct;
                    this.sizes[idx + 1] = scaledRightPct;
                });
            } else {
                // analogous logic for vertical split views
                this.initializeResizer(prevElem, nextElem, resizerElem, (ev: MouseEvent) => {
                    // we need the height of the top child in pixels
                    const topChildElemRect = prevElem.getBoundingClientRect();
                    const topChildHeight = topChildElemRect.height;

                    // we need the height of the bottom child in pixels
                    const bottomChildElemRect = nextElem.getBoundingClientRect();
                    const bottomChildHeight = bottomChildElemRect.height;

                    // when we have all that, we can calculate the new sizes of the two neighbors in percent
                    const overallHeight = topChildHeight + bottomChildHeight;
                    const newTopPct = (ev.clientY - topChildElemRect.y) / overallHeight * 100;
                    const newBottomPct = (bottomChildElemRect.y + bottomChildHeight - ev.clientY) / overallHeight * 100;

                    // we "redistribute" the old percentages between the two neighbors.
                    // the sum is also the sum of the new percentages. (does not change)
                    const neighborsPct = prevWantedPct + nextWantedPct;
                    const scaledTopPct = newTopPct / 100 * neighborsPct;
                    const scaledBottomPct = newBottomPct / 100 * neighborsPct;

                    this.sizes[idx] = scaledTopPct;
                    this.sizes[idx + 1] = scaledBottomPct;
                });

            }

        });
        this.cdr.markForCheck();
    }

    detachOutlets() {
        if (!this.viewContainerHostElements) {
            // we are running this before we did change detection, so
            // the viewContainerHostElements may not yet available or outdated.
            return;
        }
        // we actually want this to happen on an up-to-date viewContainerHostElements list,
        // but we don't want to trigger change detection, because that might trigger the
        // destruction of the view components we are trying to detach first.
        this.viewContainerHostElements.forEach((hostElem: ViewContainerRef, idx: number) => {
            while (hostElem.length > 0) {
                hostElem.detach(0);
            }
        });
    }



    updateOutlets() {
        if (!this.state) {
            // feels dirty but sometimes an update is triggered after the splitview has been destroyed
            return;
        }
        this.viewContainerHostElements.forEach((hostElem: ViewContainerRef, idx: number) => {
            // TODO: cleanup when views change
            const childViewContainerId: ViewContainerId = this.state.children[idx];
            const viewComponentRef = this.childViewContainerStates[childViewContainerId].componentRef;

            if (hostElem.length > 0) {
                hostElem.detach(0);
            }
            hostElem.insert(viewComponentRef.hostView);
        });
        this.cdr.markForCheck();
        setTimeout(() => {
            this.initializeResizers();
        });

    }

    ngOnDestroy() {
    }
}

